import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Pagination, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import { getIngredients } from "../../../baseDatos/insumos/api/ingredientsApi";
import { getPurchaseItemCategoryOptions } from "../../../baseDatos/itemsDeCompra/api/purchaseItemsApi";
import LastUpdatedIndicator from "../components/LastUpdatedIndicator";
import StockInventoryFilters from "../components/StockInventoryFilters";
import StockInventoryTable from "../components/StockInventoryTable";
import {
  getStockInventory,
  STOCK_INVENTORY_PAGE_SIZE,
} from "../api/stockInventoryApi";

const SEARCH_DEBOUNCE_MS = 700;

function unwrapHttp(response) {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    !("meta" in response)
  ) {
    return response.data;
  }
  return response;
}

function errorToMessage(error, fallback = "No se pudo cargar el inventario.") {
  const status = error?.status || error?.response?.status;
  if (status === 401 || status === 403) {
    return "No tenés permiso para consultar el inventario o la sesión expiró.";
  }

  const message =
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.detail ||
    error?.data?.message ||
    error?.message;
  return Array.isArray(message)
    ? message.join(" · ")
    : message || fallback;
}

function normalizeCategory(category) {
  if (!category?.id || !category?.name) return null;
  return { id: Number(category.id), name: String(category.name) };
}

function normalizeIngredient(ingredient) {
  if (!ingredient?.id) return null;
  const rawCategory =
    ingredient.category || ingredient.ingredientCategory || {};
  const category = normalizeCategory(rawCategory);
  const categoryName =
    category?.name ||
    (typeof ingredient.category === "string" ? ingredient.category : "") ||
    "Sin categoría";

  return {
    id: Number(ingredient.id),
    name: String(ingredient.name || "Sin nombre"),
    categoryId:
      Number(
        ingredient.categoryId ||
          ingredient.category_id ||
          ingredient.ingredientCategoryId ||
          ingredient.ingredient_category_id ||
          category?.id
      ) ||
      null,
    categoryName,
  };
}

function listFromInventory(response) {
  const payload = unwrapHttp(response);
  const items = Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.data)
    ? payload.data
    : [];

  return {
    items,
    page: Number(payload?.page) || 1,
    pageSize: Number(payload?.pageSize) || STOCK_INVENTORY_PAGE_SIZE,
    totalItems: Number(payload?.totalItems) || items.length,
    totalPages: Number(payload?.totalPages) || (items.length ? 1 : 0),
    lastUpdatedAt: payload?.lastUpdatedAt || null,
  };
}

export default function StockPage() {
  const navigate = useNavigate();
  const latestRequestId = useRef(0);
  const [items, setItems] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [filters, setFilters] = useState({
    categoryId: "",
    sector: "",
    type: "",
    ingredient: null,
  });
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [error, setError] = useState("");
  const [categoriesError, setCategoriesError] = useState("");
  const [reloadVersion, setReloadVersion] = useState(0);
  const [categoriesReloadVersion, setCategoriesReloadVersion] = useState(0);

  const selectedIngredient = filters.ingredient;
  const selectedCategory = useMemo(
    () =>
      categoryOptions.find(
        (category) => String(category.id) === String(filters.categoryId)
      ) || null,
    [categoryOptions, filters.categoryId]
  );

  const fetchInventory = useCallback(
    async (signal) => {
      const requestId = ++latestRequestId.current;
      setLoading(true);
      setItems([]);
      setTotalItems(0);
      setTotalPages(0);
      setLastUpdatedAt(null);
      setError("");

      try {
        const response = await getStockInventory({
          page,
          limit: STOCK_INVENTORY_PAGE_SIZE,
          type: filters.type || undefined,
          sector: filters.sector || undefined,
          categoryId: filters.categoryId || undefined,
          ingredientId:
            filters.type === "INGREDIENT" ? selectedIngredient?.id : undefined,
          search: search || undefined,
          signal,
        });
        if (signal?.aborted || requestId !== latestRequestId.current) return;

        const result = listFromInventory(response);
        if (result.totalPages > 0 && page > result.totalPages) {
          setPage(result.totalPages);
          return;
        }

        setItems(result.items.slice(0, STOCK_INVENTORY_PAGE_SIZE));
        setTotalItems(result.totalItems);
        setTotalPages(result.totalPages);
        setLastUpdatedAt(result.lastUpdatedAt);
      } catch (requestError) {
        if (signal?.aborted || requestId !== latestRequestId.current) return;
        setItems([]);
        setTotalItems(0);
        setTotalPages(0);
        setLastUpdatedAt(null);
        setError(errorToMessage(requestError));
      } finally {
        if (!signal?.aborted && requestId === latestRequestId.current) {
          setLoading(false);
        }
      }
    },
    [
      filters.categoryId,
      filters.sector,
      filters.type,
      page,
      search,
      selectedIngredient?.id,
    ]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchInventory(controller.signal);
    return () => controller.abort();
  }, [fetchInventory, reloadVersion]);

  useEffect(() => {
    const controller = new AbortController();

    async function fetchCategories() {
      setCategoriesLoading(true);
      setCategoriesError("");
      try {
        const response = await getPurchaseItemCategoryOptions({
          signal: controller.signal,
        });
        const payload = unwrapHttp(response);
        if (controller.signal.aborted) return;

        setCategoryOptions(
          (Array.isArray(payload) ? payload : [])
            .map(normalizeCategory)
            .filter(Boolean)
            .sort((left, right) =>
              left.name.localeCompare(right.name, "es", {
                sensitivity: "base",
              })
            )
        );
      } catch (requestError) {
        if (controller.signal.aborted) return;
        setCategoryOptions([]);
        setCategoriesError(
          errorToMessage(requestError, "No se pudieron cargar las categorías.")
        );
      } finally {
        if (!controller.signal.aborted) setCategoriesLoading(false);
      }
    }

    fetchCategories();
    return () => controller.abort();
  }, [categoriesReloadVersion]);

  const commitSearch = useCallback(
    (value) => {
      const normalized = String(value ?? "").trim();
      if (normalized === search) return;
      setSearch(normalized);
      setPage(1);
    },
    [search]
  );

  useEffect(() => {
    const timeout = setTimeout(
      () => commitSearch(searchInput),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timeout);
  }, [commitSearch, searchInput]);

  const loadIngredients = useCallback(
    ({ page: ingredientPage, limit, search: ingredientSearch, signal }) =>
      getIngredients({
        page: ingredientPage,
        limit,
        name: ingredientSearch || undefined,
        category: selectedCategory?.name || undefined,
        isActive: true,
        signal,
      }),
    [selectedCategory?.name]
  );

  const pageSummary = useMemo(() => {
    if (!totalItems) return "Sin ítems para mostrar";
    return `Mostrando ${items.length} de ${totalItems} ítems`;
  }, [items.length, totalItems]);

  const visibleError = error || categoriesError;
  const hasAppliedFilters = Boolean(
    filters.categoryId ||
      filters.sector ||
      filters.type ||
      selectedIngredient ||
      search
  );
  const emptyMessage = hasAppliedFilters
    ? "No se encontraron ítems para los filtros aplicados."
    : "Todavía no hay ítems de stock para mostrar.";

  function resetToFirstPage() {
    setPage(1);
  }

  function handleCategoryChange(value) {
    setFilters((current) => ({
      ...current,
      categoryId: value,
      type:
        value && current.type === "PREPARED_PRODUCT"
          ? "INGREDIENT"
          : current.type,
      ingredient:
        current.ingredient &&
        value &&
        String(current.ingredient.categoryId) !== String(value)
          ? null
          : current.ingredient,
    }));
    resetToFirstPage();
  }

  function handleSectorChange(value) {
    setFilters((current) => ({ ...current, sector: value }));
    resetToFirstPage();
  }

  function handleTypeChange(value) {
    setFilters((current) => ({
      ...current,
      type: value,
      categoryId:
        value === "PREPARED_PRODUCT" ? "" : current.categoryId,
      ingredient: value === "INGREDIENT" ? current.ingredient : null,
    }));
    resetToFirstPage();
  }

  function handleIngredientChange(ingredient) {
    setFilters((current) => ({ ...current, ingredient }));
    resetToFirstPage();
  }

  function handleSearchClear() {
    setSearchInput("");
    setSearch("");
    resetToFirstPage();
  }

  function handleRetry() {
    setReloadVersion((current) => current + 1);
    setCategoriesReloadVersion((current) => current + 1);
  }

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        overflow: { xs: "auto", md: "hidden" },
      }}
    >
      <StockPageHeader title="Stock" onBack={() => navigate(-1)} />

      <Box
        sx={{
          p: 2,
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          bgcolor: "neutral.300",
          borderRadius: "30px",
          boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.5)",
        }}
      >
        <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={1.5}
            sx={{
              alignItems: { xs: "stretch", lg: "center" },
              justifyContent: "space-between",
            }}
          >
            <StockInventoryFilters
              categoryOptions={categoryOptions}
              categoriesLoading={categoriesLoading}
              categoryValue={filters.categoryId}
              sectorValue={filters.sector}
              typeValue={filters.type}
              ingredientValue={selectedIngredient}
              searchInput={searchInput}
              loadIngredients={loadIngredients}
              normalizeIngredient={normalizeIngredient}
              onCategoryChange={handleCategoryChange}
              onSectorChange={handleSectorChange}
              onTypeChange={handleTypeChange}
              onIngredientChange={handleIngredientChange}
              onSearchInputChange={setSearchInput}
              onSearchCommit={commitSearch}
              onSearchClear={handleSearchClear}
            />
          </Stack>

          {visibleError ? (
            <Box
              role="alert"
              sx={{
                px: 2,
                py: 1.2,
                borderRadius: "18px",
                bgcolor: "secondary.700",
                boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.35)",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: "logoblanco" }}>
                    No se pudo consultar el inventario
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "logoblanco", opacity: 0.95 }}>
                    {visibleError}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={handleRetry}
                  disabled={loading}
                  sx={{ color: "logoblanco", textTransform: "none", fontWeight: 800 }}
                >
                  Reintentar
                </Button>
              </Stack>
            </Box>
          ) : null}

          <StockInventoryTable
            rows={items}
            loading={loading}
            emptyMessage={emptyMessage}
          />

          <Stack
            direction="row"
            spacing={1.2}
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              pt: 0.5,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: "primary.900",
                minWidth: 0,
              }}
            >
              {loading && items.length
                ? "Actualizando inventario..."
                : loading
                ? "Cargando inventario..."
                : pageSummary}
            </Typography>
            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                alignItems: "center",
                justifyContent: "flex-end",
                ml: "auto",
                minWidth: 0,
              }}
            >
              <Pagination
                page={page}
                count={Math.max(totalPages, 1)}
                onChange={(_, value) => setPage(value)}
                shape="rounded"
                color="primary"
                disabled={loading || totalPages <= 1}
                sx={{ flexShrink: 0 }}
              />
              <LastUpdatedIndicator lastUpdatedAt={lastUpdatedAt} />
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}
